import type { ElementType } from 'react';
import { Building2, Camera, Gem, PenLine, UsersRound } from 'lucide-react';

export type TrustRecordType =
  | 'story'
  | 'achievement'
  | 'milestone'
  | 'institution_record'
  | 'journey_item';

export type TrustOwnerType = 'individual' | 'institution';

export type TrustVerificationLevel =
  | 'self_documented'
  | 'media_supported'
  | 'community_confirmed'
  | 'institution_recognized'
  | 'ledger_verified';

export type TrustVerifiedByType = 'self' | 'community' | 'institution' | 'system' | 'admin';

export type TrustVisibility = 'private' | 'shared' | 'public';

export type SupportingMaterial = {
  id: string;
  trust_record_id: string;
  type: 'photo' | 'video' | 'audio' | 'certificate' | 'document' | 'link' | 'archive_item';
  title: string;
  description?: string;
  file_url?: string;
  thumbnail_url?: string;
  visibility: TrustVisibility;
  uploaded_by?: string;
  uploaded_at?: string;
  verified_status?: 'pending' | 'reviewed' | 'recognized';
  metadata?: Record<string, unknown>;
};

export type CommunityConfirmation = {
  id: string;
  trust_record_id: string;
  confirmer_id?: string;
  confirmer_name: string;
  relationship_to_moment?: string;
  confirmation_text?: string;
  visibility: TrustVisibility;
  created_at: string;
};

export type InstitutionRecognition = {
  id: string;
  trust_record_id: string;
  institution_id?: string;
  institution_name: string;
  approver_name?: string;
  approver_role?: string;
  recognition_text?: string;
  recognition_date?: string;
  certificate_url?: string;
  visibility: TrustVisibility;
  created_at: string;
};

export type TrustRecord = {
  id: string;
  record_id: string;
  record_type: TrustRecordType;
  owner_id: string;
  owner_type: TrustOwnerType;
  verification_level: TrustVerificationLevel;
  verified_by_type: TrustVerifiedByType;
  verified_by_id?: string;
  verified_by_name: string;
  verification_date?: string;
  event_date?: string;
  location_name?: string;
  category?: string;
  pillar?: string;
  title?: string;
  summary?: string;
  supporting_materials: SupportingMaterial[];
  media_count: number;
  document_count: number;
  certificate_count: number;
  community_confirmation_count: number;
  institution_recognition_count: number;
  record_id_public: string;
  visibility: TrustVisibility;
  private_files_enabled: boolean;
  public_files_enabled: boolean;
  linked_story_ids: string[];
  linked_achievement_ids: string[];
  linked_journey_item_ids: string[];
  linked_institution_ids: string[];
  community_confirmations: CommunityConfirmation[];
  institution_recognitions: InstitutionRecognition[];
  audit_history: { label: string; at: string; actor?: string }[];
  created_at: string;
  updated_at: string;
};

export const TRUST_LEVEL_COPY: Record<
  TrustVerificationLevel,
  {
    label: string;
    meaning: string;
    tooltip: string;
    Icon: ElementType;
    badgeClassName: string;
    dotColor: string;
  }
> = {
  self_documented: {
    label: 'Self Documented',
    meaning: 'The owner created this record themselves.',
    tooltip: 'This moment was documented by the owner.',
    Icon: PenLine,
    badgeClassName: 'bg-slate-100 text-slate-700 border-slate-200',
    dotColor: '#94a3b8',
  },
  media_supported: {
    label: 'Media Supported',
    meaning: 'This moment includes supporting media or files.',
    tooltip: 'This moment includes supporting media or files.',
    Icon: Camera,
    badgeClassName: 'bg-sky-100 text-sky-700 border-sky-200',
    dotColor: '#38bdf8',
  },
  community_confirmed: {
    label: 'Community Confirmed',
    meaning: 'People connected to the moment confirmed or recognized it.',
    tooltip: 'This moment has been confirmed or recognized by people connected to it.',
    Icon: UsersRound,
    badgeClassName: 'bg-violet-100 text-violet-700 border-violet-200',
    dotColor: '#a78bfa',
  },
  institution_recognized: {
    label: 'Institution Recognized',
    meaning: 'An institution or organization has recognized this record.',
    tooltip: 'This moment has been recognized by an institution or organization.',
    Icon: Building2,
    badgeClassName: 'bg-amber-100 text-amber-800 border-amber-200',
    dotColor: '#f59e0b',
  },
  ledger_verified: {
    label: 'Ledger Verified',
    meaning: 'Structured documentation and recognized validation support this record.',
    tooltip: 'This record includes structured documentation and recognized validation.',
    Icon: Gem,
    badgeClassName: 'text-slate-900 border-cyan-200',
    dotColor: '#14b8a6',
  },
};

export function deriveStoryTitle(story?: string, fallback = 'Ertzyx Moment') {
  const clean = (story || '').replace(/\s+/g, ' ').trim();
  if (!clean) return fallback;
  const firstSentence = clean.split(/[.!?]/)[0]?.trim();
  const source = firstSentence || clean;
  return source.length > 58 ? `${source.slice(0, 55).trim()}...` : source;
}

export function createStoryTrustRecord(post: any): TrustRecord {
  const mediaUrl = post.media_url || post.image_url;
  const hasMedia =
    !!mediaUrl ||
    ['photo', 'image', 'video', 'audio', 'voice', 'document', 'link'].includes(post.content_type);
  const isInstitution = post.is_institution || post.user_profiles?.role === 'institution';
  const witnessCount = Number(post.witness_count || 0);
  const isVerified = Boolean(post.is_verified);

  let verificationLevel: TrustVerificationLevel = 'self_documented';
  if (hasMedia) verificationLevel = 'media_supported';
  if (witnessCount > 0) verificationLevel = 'community_confirmed';
  if (isInstitution || isVerified) verificationLevel = 'institution_recognized';
  if ((isVerified && hasMedia) || (isInstitution && witnessCount > 0 && hasMedia)) {
    verificationLevel = 'ledger_verified';
  }

  const mediaType =
    post.content_type === 'video'
      ? 'video'
      : post.content_type === 'audio' || post.content_type === 'voice'
        ? 'audio'
        : post.content_type === 'document'
          ? 'document'
          : post.content_type === 'link'
            ? 'link'
            : 'photo';

  const supportingMaterials: SupportingMaterial[] = hasMedia
    ? [
        {
          id: `${post.id}-material-1`,
          trust_record_id: `TR-${post.id}`,
          type: mediaType,
          title:
            mediaType === 'document'
              ? 'Shared document'
              : mediaType === 'link'
                ? 'Public link'
                : 'Public media',
          file_url: mediaUrl,
          thumbnail_url: mediaType === 'photo' ? mediaUrl : undefined,
          visibility: 'public',
          uploaded_by: post.author_name || post.user_profiles?.full_name || 'Owner',
          uploaded_at: post.created_at,
          verified_status: verificationLevel === 'ledger_verified' ? 'recognized' : 'reviewed',
        },
      ]
    : [];

  const communityConfirmations: CommunityConfirmation[] =
    witnessCount > 0
      ? [
          {
            id: `${post.id}-community`,
            trust_record_id: `TR-${post.id}`,
            confirmer_name: 'Ertzyx community',
            relationship_to_moment: 'Witnesses',
            confirmation_text: `Confirmed by ${witnessCount} ${witnessCount === 1 ? 'person' : 'people'}.`,
            visibility: 'public',
            created_at: post.created_at,
          },
        ]
      : [];

  const institutionRecognitions: InstitutionRecognition[] = isInstitution
    ? [
        {
          id: `${post.id}-institution`,
          trust_record_id: `TR-${post.id}`,
          institution_id: post.user_id,
          institution_name: post.author_name || post.user_profiles?.full_name || 'Institution',
          recognition_text: 'Published by an institution profile.',
          visibility: 'public',
          created_at: post.created_at,
        },
      ]
    : [];

  return {
    id: `TR-${post.id}`,
    record_id: post.id,
    record_type: 'story',
    owner_id: post.user_id,
    owner_type: isInstitution ? 'institution' : 'individual',
    verification_level: verificationLevel,
    verified_by_type:
      verificationLevel === 'ledger_verified'
        ? 'system'
        : isInstitution
          ? 'institution'
          : witnessCount > 0
            ? 'community'
            : 'self',
    verified_by_id: isInstitution ? post.user_id : undefined,
    verified_by_name: isInstitution
      ? post.author_name || post.user_profiles?.full_name || 'Institution'
      : witnessCount > 0
        ? 'Ertzyx Community Recognition System'
        : 'User-submitted documentation',
    verification_date: post.created_at,
    event_date: post.event_date || post.created_at,
    location_name: post.location_name || post.location || undefined,
    category: post.category || post.pillar_tag,
    pillar: post.pillar_tag,
    title: deriveStoryTitle(post.story),
    summary: post.story,
    supporting_materials: supportingMaterials,
    media_count: hasMedia && mediaType !== 'document' ? 1 : 0,
    document_count: mediaType === 'document' || mediaType === 'link' ? 1 : 0,
    certificate_count: 0,
    community_confirmation_count: witnessCount,
    institution_recognition_count: institutionRecognitions.length,
    record_id_public: `ERT-${post.id.slice(0, 8).toUpperCase()}`,
    visibility: 'public',
    private_files_enabled: false,
    public_files_enabled: hasMedia,
    linked_story_ids: [post.id],
    linked_achievement_ids: [],
    linked_journey_item_ids: [],
    linked_institution_ids: isInstitution ? [post.user_id] : [],
    community_confirmations: communityConfirmations,
    institution_recognitions: institutionRecognitions,
    audit_history: [
      {
        label: 'Record added to Ertzyx',
        at: post.created_at,
        actor: post.author_name || post.user_profiles?.full_name || 'Owner',
      },
    ],
    created_at: post.created_at,
    updated_at: post.updated_at || post.created_at,
  };
}

export function createAchievementTrustRecord(ver: any): TrustRecord {
  const isLedger = ver.verification_type === 'gold' || Boolean(ver.verification_ref);
  const createdAt = ver.verified_at || ver.created_at || new Date().toISOString();
  return {
    id: `TR-${ver.id}`,
    record_id: ver.id,
    record_type: 'achievement',
    owner_id: ver.user_id || ver.individual_id || '',
    owner_type: 'individual',
    verification_level: isLedger ? 'ledger_verified' : 'institution_recognized',
    verified_by_type: 'institution',
    verified_by_id: ver.institution_id,
    verified_by_name: ver.verified_by || ver.institution || 'Institution admin',
    verification_date: createdAt,
    event_date: ver.event_date || createdAt,
    location_name: ver.location_name,
    category: ver.category || ver.pillar,
    pillar: ver.pillar,
    title: ver.title || ver.record_title || 'Verified record',
    summary: ver.description || ver.notes || 'Recognized as part of the Ertzyx Ledger.',
    supporting_materials: [],
    media_count: 0,
    document_count: 0,
    certificate_count: isLedger ? 1 : 0,
    community_confirmation_count: 0,
    institution_recognition_count: 1,
    record_id_public: ver.verification_ref || `ERT-${String(ver.id).slice(0, 8).toUpperCase()}`,
    visibility: 'public',
    private_files_enabled: true,
    public_files_enabled: Boolean(ver.certificate_url),
    linked_story_ids: [],
    linked_achievement_ids: [ver.achievement_id || ver.id],
    linked_journey_item_ids: [],
    linked_institution_ids: ver.institution_id ? [ver.institution_id] : [],
    community_confirmations: [],
    institution_recognitions: [
      {
        id: `${ver.id}-recognition`,
        trust_record_id: `TR-${ver.id}`,
        institution_id: ver.institution_id,
        institution_name: ver.verified_by || ver.institution || 'Institution',
        recognition_text: ver.notes || 'Institution recognition recorded.',
        recognition_date: createdAt,
        certificate_url: ver.certificate_url,
        visibility: 'public',
        created_at: createdAt,
      },
    ],
    audit_history: [{ label: 'Institution recognition recorded', at: createdAt }],
    created_at: createdAt,
    updated_at: ver.updated_at || createdAt,
  };
}

export function createInstitutionRecordTrustRecord(record: any): TrustRecord {
  const createdAt = record.created_at || new Date().toISOString();
  const isGold = record.verification_status === 'gold';
  return {
    id: `TR-${record.id}`,
    record_id: record.id,
    record_type: 'institution_record',
    owner_id: record.institution_id || '',
    owner_type: 'institution',
    verification_level: isGold ? 'ledger_verified' : 'institution_recognized',
    verified_by_type: 'institution',
    verified_by_id: record.institution_id,
    verified_by_name: record.archived_by || 'Institution admin',
    verification_date: createdAt,
    event_date: record.date_of_record || createdAt,
    location_name: record.location_name,
    category: record.category || 'Heritage Record',
    pillar: record.pillar,
    title: record.record_title || 'Institution record',
    summary: record.historical_context || 'Preserved in the institution heritage archive.',
    supporting_materials: [],
    media_count: 0,
    document_count: 1,
    certificate_count: isGold ? 1 : 0,
    community_confirmation_count: 0,
    institution_recognition_count: 1,
    record_id_public: `INST-REC-${String(record.id).slice(0, 8).toUpperCase()}`,
    visibility: 'public',
    private_files_enabled: true,
    public_files_enabled: false,
    linked_story_ids: [],
    linked_achievement_ids: [],
    linked_journey_item_ids: [],
    linked_institution_ids: record.institution_id ? [record.institution_id] : [],
    community_confirmations: [],
    institution_recognitions: [
      {
        id: `${record.id}-archive`,
        trust_record_id: `TR-${record.id}`,
        institution_id: record.institution_id,
        institution_name: record.archived_by || 'Institution',
        recognition_text: 'Archived as an institutional record.',
        recognition_date: createdAt,
        visibility: 'public',
        created_at: createdAt,
      },
    ],
    audit_history: [{ label: 'Added to institutional archive', at: createdAt }],
    created_at: createdAt,
    updated_at: record.updated_at || createdAt,
  };
}

export const trustLevelOrder: TrustVerificationLevel[] = [
  'self_documented',
  'media_supported',
  'community_confirmed',
  'institution_recognized',
  'ledger_verified',
];

export function trustLevelIndex(level: TrustVerificationLevel) {
  return Math.max(0, trustLevelOrder.indexOf(level));
}
